import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Shield, ArrowLeft, Search, Download, UserCog, X,
  ChevronLeft, ChevronRight, Calendar, DollarSign, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminCustomers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('all');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [overrideForm, setOverrideForm] = useState({
    subscription_tier: '',
    custom_price: '',
    subscription_end_date: '',
    notes: ''
  });
  const [cancelForm, setCancelForm] = useState({
    cancel_at: '',
    reason: ''
  });

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('admin_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;
      if (tierFilter !== 'all') params.tier = tierFilter;

      const response = await axios.get(`${API}/admin/customers`, {
        headers: getAuthHeaders(),
        params
      });
      setCustomers(response.data.customers || []);
      setTotal(response.data.total || 0);
      // Backend doesn't support pagination yet, calculate pages from total
      setPages(Math.ceil((response.data.total || 0) / 20) || 1);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch customers');
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, tierFilter, getAuthHeaders, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchCustomers();
  }, [navigate, fetchCustomers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchCustomers();
  };

  const handleViewCustomer = async (customerId) => {
    try {
      const response = await axios.get(`${API}/admin/customers/${customerId}`, {
        headers: getAuthHeaders()
      });
      setSelectedCustomer(response.data);
    } catch (error) {
      toast.error('Failed to fetch customer details');
    }
  };

  const handleOverrideSubscription = async () => {
    if (!selectedCustomer) return;
    
    try {
      const payload = {
        subscription_tier: overrideForm.subscription_tier,
        notes: overrideForm.notes || null
      };
      if (overrideForm.custom_price) {
        payload.custom_price = parseFloat(overrideForm.custom_price);
      }
      if (overrideForm.subscription_end_date) {
        payload.subscription_end_date = overrideForm.subscription_end_date;
      }

      await axios.put(
        `${API}/admin/customers/${selectedCustomer.customer.id}/subscription`,
        payload,
        { headers: getAuthHeaders() }
      );
      
      toast.success('Subscription updated');
      setShowOverrideModal(false);
      handleViewCustomer(selectedCustomer.customer.id);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    }
  };

  const handleScheduleCancel = async () => {
    if (!selectedCustomer) return;
    
    try {
      await axios.post(
        `${API}/admin/customers/${selectedCustomer.customer.id}/cancel`,
        {
          cancel_at: cancelForm.cancel_at || null,
          reason: cancelForm.reason || 'Admin initiated'
        },
        { headers: getAuthHeaders() }
      );
      
      toast.success('Cancellation scheduled');
      setShowCancelModal(false);
      handleViewCustomer(selectedCustomer.customer.id);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to schedule cancellation');
    }
  };

  const handleExportData = async (customerId) => {
    try {
      const response = await axios.get(
        `${API}/admin/customers/${customerId}/export`,
        { headers: getAuthHeaders() }
      );
      
      // Download CSV
      const blob = new Blob([response.data.csv_content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.filename;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Data exported');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const getTierBadge = (tier) => {
    const colors = {
      free: 'bg-slate-600',
      solo: 'bg-[#FF5F00]',
      crew: 'bg-purple-600'
    };
    const names = {
      free: 'Side Hustle',
      solo: 'Solo Pro',
      crew: 'Crew'
    };
    return (
      <span className={`${colors[tier] || 'bg-slate-600'} text-white text-xs font-bold px-2 py-1 rounded-full`}>
        {names[tier] || tier}
      </span>
    );
  };

  return (
    <>
      <meta name="robots" content="noindex, nofollow" />
      
      <div className="min-h-screen bg-slate-900">
        {/* Header */}
        <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/dashboard')}
                className="text-slate-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center gap-2">
                <UserCog className="w-6 h-6 text-red-500" />
                <h1 className="text-xl font-bold text-white">Customer Management</h1>
              </div>
            </div>
          </div>
        </header>

        <main className="p-6">
          {/* Search and Filters */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4 mb-6">
            <form onSubmit={handleSearch} className="flex gap-4 items-end">
              <div className="flex-1">
                <Label className="text-slate-400 text-xs mb-1">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Email, name, or business name..."
                    className="pl-10 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
              </div>
              <div className="w-48">
                <Label className="text-slate-400 text-xs mb-1">Subscription</Label>
                <Select value={tierFilter} onValueChange={setTierFilter}>
                  <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tiers</SelectItem>
                    <SelectItem value="free">Side Hustle</SelectItem>
                    <SelectItem value="solo">Solo Pro</SelectItem>
                    <SelectItem value="crew">Crew</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="bg-red-600 hover:bg-red-700">
                Search
              </Button>
            </form>
          </div>

          {/* Results */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between">
              <span className="text-slate-400 text-sm">{total} customers found</span>
            </div>
            
            {loading ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : (
              <div className="divide-y divide-slate-700">
                {customers.map(customer => (
                  <div 
                    key={customer.id}
                    className="p-4 hover:bg-slate-700/50 cursor-pointer transition-colors"
                    onClick={() => handleViewCustomer(customer.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold">
                            {customer.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-white">{customer.name || 'Unnamed'}</p>
                          <p className="text-sm text-slate-400">{customer.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-sm text-slate-400">{customer.business_name || 'No business'}</p>
                          <p className="text-xs text-slate-500">{customer.job_count || 0} jobs</p>
                        </div>
                        {getTierBadge(customer.subscription_tier)}
                        {customer.cancelled && (
                          <span className="bg-red-900 text-red-300 text-xs font-bold px-2 py-1 rounded-full">
                            Cancelled
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination */}
            {pages > 1 && (
              <div className="p-4 border-t border-slate-700 flex items-center justify-center gap-2">
                <Button
                  variant="ghost"
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="text-slate-400"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-slate-400 text-sm">
                  Page {page} of {pages}
                </span>
                <Button
                  variant="ghost"
                  disabled={page === pages}
                  onClick={() => setPage(p => p + 1)}
                  className="text-slate-400"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </main>

        {/* Customer Detail Modal */}
        <Dialog open={!!selectedCustomer} onOpenChange={() => setSelectedCustomer(null)}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3">
                <div className="w-12 h-12 bg-slate-600 rounded-full flex items-center justify-center">
                  <span className="text-xl font-bold">
                    {selectedCustomer?.customer?.name?.charAt(0)?.toUpperCase() || '?'}
                  </span>
                </div>
                <div>
                  <p className="text-xl font-bold">{selectedCustomer?.customer?.name}</p>
                  <p className="text-sm text-slate-400 font-normal">{selectedCustomer?.customer?.email}</p>
                </div>
              </DialogTitle>
            </DialogHeader>

            {selectedCustomer && (
              <div className="space-y-6 mt-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-xs uppercase mb-1">Business</p>
                    <p className="text-white">{selectedCustomer.customer.business_name || 'N/A'}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-xs uppercase mb-1">Subscription</p>
                    <div className="flex items-center gap-2">
                      {getTierBadge(selectedCustomer.customer.subscription_tier)}
                      {selectedCustomer.customer.custom_price && (
                        <span className="text-amber-400 text-sm">
                          (Custom: ${selectedCustomer.customer.custom_price}/mo)
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-xs uppercase mb-1">Jobs</p>
                    <p className="text-white text-xl font-bold">{selectedCustomer.stats?.total_jobs || 0}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-xs uppercase mb-1">Total Revenue</p>
                    <p className="text-emerald-400 text-xl font-bold">
                      ${selectedCustomer.stats?.total_revenue?.toFixed(2) || '0.00'}
                    </p>
                  </div>
                </div>

                {/* Job Status Breakdown */}
                {selectedCustomer.jobs && selectedCustomer.jobs.length > 0 && (
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <p className="text-slate-400 text-xs uppercase mb-3">Jobs by Status</p>
                  <div className="flex gap-4">
                    {Object.entries(
                      selectedCustomer.jobs.reduce((acc, job) => {
                        const status = job.status || 'unknown';
                        acc[status] = (acc[status] || 0) + 1;
                        return acc;
                      }, {})
                    ).map(([status, count]) => (
                      <div key={status} className="text-center">
                        <p className="text-2xl font-bold text-white">{count}</p>
                        <p className="text-xs text-slate-400 capitalize">{status}</p>
                      </div>
                    ))}
                  </div>
                </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 flex-wrap">
                  <Button
                    onClick={() => {
                      setOverrideForm({
                        subscription_tier: selectedCustomer.customer.subscription_tier,
                        custom_price: selectedCustomer.customer.custom_price || '',
                        subscription_end_date: '',
                        notes: ''
                      });
                      setShowOverrideModal(true);
                    }}
                    className="bg-[#FF5F00] hover:bg-orange-600"
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Override Subscription
                  </Button>
                  <Button
                    onClick={() => {
                      setCancelForm({ cancel_at: '', reason: '' });
                      setShowCancelModal(true);
                    }}
                    variant="outline"
                    className="border-red-600 text-red-400 hover:bg-red-900/20"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Cancellation
                  </Button>
                  <Button
                    onClick={() => handleExportData(selectedCustomer.customer.id)}
                    variant="outline"
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Data
                  </Button>
                </div>

                {/* Subscription Override Info */}
                {selectedCustomer.customer.subscription_override && (
                  <div className="bg-amber-900/20 border border-amber-700 rounded-lg p-4">
                    <p className="text-amber-400 text-xs uppercase mb-1">Subscription Override Active</p>
                    <p className="text-sm text-slate-300">
                      {selectedCustomer.customer.subscription_override.notes || 'No notes'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Overridden: {new Date(selectedCustomer.customer.subscription_override.overridden_at).toLocaleString()}
                    </p>
                  </div>
                )}

                {/* Cancellation Info */}
                {selectedCustomer.customer.cancelled && (
                  <div className="bg-red-900/20 border border-red-700 rounded-lg p-4">
                    <p className="text-red-400 text-xs uppercase mb-1">Cancellation Scheduled</p>
                    <p className="text-sm text-slate-300">
                      Reason: {selectedCustomer.customer.cancellation?.reason || 'N/A'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Ends: {selectedCustomer.customer.subscription_end_date || 'Immediately'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Override Subscription Modal */}
        <Dialog open={showOverrideModal} onOpenChange={setShowOverrideModal}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Override Subscription</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-400">Subscription Tier</Label>
                <Select 
                  value={overrideForm.subscription_tier} 
                  onValueChange={(v) => setOverrideForm(p => ({ ...p, subscription_tier: v }))}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Side Hustle (Free)</SelectItem>
                    <SelectItem value="solo">Solo Pro ($19/mo)</SelectItem>
                    <SelectItem value="crew">Crew ($49/mo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400">Custom Price (optional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={overrideForm.custom_price}
                  onChange={(e) => setOverrideForm(p => ({ ...p, custom_price: e.target.value }))}
                  placeholder="Leave empty for standard pricing"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label className="text-slate-400">Subscription End Date (optional)</Label>
                <Input
                  type="date"
                  value={overrideForm.subscription_end_date}
                  onChange={(e) => setOverrideForm(p => ({ ...p, subscription_end_date: e.target.value }))}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label className="text-slate-400">Notes</Label>
                <Input
                  value={overrideForm.notes}
                  onChange={(e) => setOverrideForm(p => ({ ...p, notes: e.target.value }))}
                  placeholder="Reason for override..."
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <Button onClick={handleOverrideSubscription} className="w-full bg-[#FF5F00]">
                Apply Override
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Cancel Subscription Modal */}
        <Dialog open={showCancelModal} onOpenChange={setShowCancelModal}>
          <DialogContent className="bg-slate-800 border-slate-700 text-white">
            <DialogHeader>
              <DialogTitle>Schedule Cancellation</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-slate-400">Cancel At (leave empty for immediate)</Label>
                <Input
                  type="date"
                  value={cancelForm.cancel_at}
                  onChange={(e) => setCancelForm(p => ({ ...p, cancel_at: e.target.value }))}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div>
                <Label className="text-slate-400">Reason</Label>
                <Input
                  value={cancelForm.reason}
                  onChange={(e) => setCancelForm(p => ({ ...p, reason: e.target.value }))}
                  placeholder="Reason for cancellation..."
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <Button onClick={handleScheduleCancel} className="w-full bg-red-600 hover:bg-red-700">
                Schedule Cancellation
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
